import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Building2, Flame, CheckCircle2, Clock, XCircle, FileQuestion,
  Edit3, Save, Search, Filter, RefreshCw, Upload, Trash2, Image as ImageIcon,
  Eye, Settings2, Plus, ShieldCheck, FileSpreadsheet,
} from "lucide-react";
import api, { API, getToken } from "@/lib/api";
import HijriDatePicker from "@/components/HijriDatePicker";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";

/* ─────────── Small helpers ─────────── */
const ICONS = { Building2, Flame, ShieldCheck };
const COLOR_PALETTE = {
  indigo: { text: "text-indigo-700", ring: "ring-indigo-400", border: "border-indigo-300", bg: "bg-indigo-50/30", icon: "text-indigo-600" },
  rose:   { text: "text-rose-700",   ring: "ring-rose-400",   border: "border-rose-300",   bg: "bg-rose-50/30",   icon: "text-rose-600" },
  emerald:{ text: "text-emerald-700",ring: "ring-emerald-400",border: "border-emerald-300",bg: "bg-emerald-50/30",icon: "text-emerald-600" },
  amber:  { text: "text-amber-700",  ring: "ring-amber-400",  border: "border-amber-300",  bg: "bg-amber-50/30",  icon: "text-amber-600" },
  sky:    { text: "text-sky-700",    ring: "ring-sky-400",    border: "border-sky-300",    bg: "bg-sky-50/30",    icon: "text-sky-600" },
  slate:  { text: "text-slate-700",  ring: "ring-slate-400",  border: "border-slate-300",  bg: "bg-slate-50/30",  icon: "text-slate-600" },
};
const colorOf = (c) => COLOR_PALETTE[c] || COLOR_PALETTE.indigo;
const iconOf  = (name) => ICONS[name] || Building2;

const statusOf = (days) => {
  if (days === null || days === undefined) return "missing";
  if (days < 0) return "expired";
  if (days <= 30) return "warning";
  return "valid";
};
const formatH = (h) => (!h ? "—" : `${String(h.d).padStart(2, "0")}/${String(h.m).padStart(2, "0")}/${h.y}`);
const daysUntil = (g) => (!g ? null : Math.ceil((new Date(g) - new Date()) / 86400000));

function Pill({ days, t }) {
  const s = statusOf(days);
  if (s === "missing") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-500 border border-slate-200"><FileQuestion size={11} /> {t("license_status_missing")}</span>;
  if (s === "expired") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-700 border border-rose-300"><XCircle size={11} /> {t("license_status_expired_days", { n: Math.abs(days) })}</span>;
  if (s === "warning") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-300"><Clock size={11} /> {t("license_status_soon_days", { n: days })}</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300"><CheckCircle2 size={11} /> {t("license_status_valid_days", { n: days })}</span>;
}

/* ─────────── Attachment slot ─────────── */
function AttachmentSlot({ branchId, kind, existing, onChanged, label, colorClass, t }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();
  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const preview = existing ? `${backendBase}/api/branches/${branchId}/license-attachments/${existing.id}` : null;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      await api.post(`/branches/${branchId}/license-attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChanged();
    } catch (e) { window.alert(e.response?.data?.detail || e.message); }
    finally { setUploading(false); }
  };
  const remove = async () => {
    if (!existing || !window.confirm(t("license_confirm_delete_image"))) return;
    try {
      await api.delete(`/branches/${branchId}/license-attachments/${existing.id}`);
      onChanged();
    } catch (e) { window.alert(e.response?.data?.detail || e.message); }
  };
  return (
    <div className={`rounded-xl border-2 border-dashed p-3 ${colorClass}`}>
      <div className="text-xs font-bold mb-2 flex items-center gap-1"><ImageIcon size={13} /> {label}</div>
      {existing ? (
        <div className="space-y-2">
          <div className="relative bg-white rounded-lg overflow-hidden border border-slate-200">
            {existing.content_type?.startsWith("image/") ? (
              <img src={preview} alt={label} className="w-full h-32 object-cover" />
            ) : (
              <div className="h-32 flex items-center justify-center text-slate-400 text-xs">{existing.filename}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <a href={preview} target="_blank" rel="noopener noreferrer" className="flex-1 btn btn-secondary text-xs justify-center" data-testid={`license-att-view-${kind}`}>
              <Eye size={12} /> {t("license_view")}
            </a>
            <button onClick={remove} className="btn text-xs bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100" data-testid={`license-att-delete-${kind}`}>
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      ) : (
        <>
          <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`license-att-input-${kind}`} />
          <button onClick={() => inputRef.current?.click()} disabled={uploading} className="w-full py-4 rounded-lg bg-white border border-slate-200 hover:border-teal-400 text-xs text-slate-500 hover:text-teal-700 flex flex-col items-center gap-1" data-testid={`license-att-upload-${kind}`}>
            <Upload size={16} />{uploading ? "…" : t("license_upload_image")}
          </button>
        </>
      )}
    </div>
  );
}

/* ─────────── Edit modal (dynamic license fields) ─────────── */
function LicenseEditModal({ branch, licenseTypes, onClose, onSaved, t, lang }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [current, setCurrent] = useState(branch);
  const [form, setForm] = useState(() => {
    const m = {};
    for (const lt of licenseTypes) {
      m[lt.key] = (branch.licenses?.[lt.key]?.expiry_hijri) || null;
    }
    return { licenses: m, license_notes: branch.license_notes || "" };
  });

  const refreshBranch = useCallback(async () => {
    try {
      const res = await api.get("/branches");
      const b = res.data.find((x) => x.id === branch.id);
      if (b) setCurrent(b);
    } catch { /* ignore */ }
  }, [branch.id]);

  const validHijri = (h) => h === null || (h && h.d && h.m && h.y);

  const save = async () => {
    setErr(null);
    if (!Object.values(form.licenses).every(validHijri)) {
      setErr(t("license_err_incomplete_date"));
      return;
    }
    setSaving(true);
    try {
      const licensesPayload = {};
      for (const lt of licenseTypes) {
        licensesPayload[lt.key] = { expiry_hijri: form.licenses[lt.key] || null };
      }
      await api.put(`/branches/${branch.id}`, {
        code: branch.code,
        name: branch.name,
        admin_id: branch.admin_id,
        is_vacation: branch.is_vacation,
        is_admin: branch.is_admin,
        licenses: licensesPayload,
        license_notes: form.license_notes,
      });
      onSaved();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="license-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
        <div className="bg-gradient-to-l from-indigo-600 to-fuchsia-600 text-white px-4 py-3 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2">
            <Building2 size={16} /> {t("license_modal_title")} — {branch.name} {branch.code && <span className="text-white/70">[{branch.code}]</span>}
          </h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-license-edit">✕</button>
        </div>
        <div className="p-5 space-y-5 max-h-[80vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {licenseTypes.map((lt) => {
              const Icon = iconOf(lt.icon);
              const c = colorOf(lt.color);
              const attKey = lt.key === "business" ? "business_license" : (lt.key === "civil_defense" ? "civil_defense" : lt.key);
              const att = (current.license_attachments || []).find((a) => a.kind === attKey);
              return (
                <section key={lt.key}>
                  <h4 className={`text-sm font-bold ${c.text} mb-3 border-r-4 pr-2 flex items-center gap-1`} style={{ borderColor: "currentColor" }}>
                    <Icon size={14} /> {lang === "ar" ? lt.name_ar : lt.name_en}
                  </h4>
                  <HijriDatePicker
                    label={t("expiry_date")}
                    value={form.licenses[lt.key]}
                    onChange={(v) => setForm({ ...form, licenses: { ...form.licenses, [lt.key]: v } })}
                    testid={`lic-${lt.key}-expiry`}
                  />
                  <div className="mt-3">
                    <AttachmentSlot
                      branchId={branch.id}
                      kind={attKey}
                      existing={att}
                      onChanged={refreshBranch}
                      label={(lang === "ar" ? "صورة — " : "Image — ") + (lang === "ar" ? lt.name_ar : lt.name_en)}
                      colorClass={`${c.border} ${c.bg}`}
                      t={t}
                    />
                  </div>
                </section>
              );
            })}
          </div>
          <div>
            <label className="text-sm font-bold text-slate-700 mb-2 block">{t("license_notes_label")}</label>
            <textarea rows={2} value={form.license_notes} onChange={(e) => setForm({ ...form, license_notes: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder={t("license_notes_placeholder")} data-testid="license-notes-input" />
          </div>
          {err && <div className="text-sm bg-rose-50 border border-rose-200 rounded-lg p-2 text-rose-700" data-testid="license-edit-error">{typeof err === "string" ? err : JSON.stringify(err)}</div>}
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary" data-testid="license-cancel">{t("cancel")}</button>
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="license-save"><Save size={14} /> {saving ? "…" : t("save")}</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Manage License Types Modal (super_admin only) ─────────── */
function ManageLicenseTypesModal({ types, onClose, onChanged, t, lang }) {
  const [items, setItems] = useState(types);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const emptyRow = () => ({ _uid: Math.random().toString(36).slice(2), name_ar: "", name_en: "", color: "sky", icon: "Building2" });
  const [newRows, setNewRows] = useState([emptyRow()]);

  const patchLocal = (key, patch) => setItems((arr) => arr.map((x) => x.key === key ? { ...x, ...patch } : x));

  const saveOne = async (lt) => {
    setBusy(true); setErr(null);
    try {
      await api.patch(`/license-types/${lt.key}`, { name_ar: lt.name_ar, name_en: lt.name_en, color: lt.color, icon: lt.icon, order: lt.order });
      await onChanged();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const deleteOne = async (lt) => {
    if (!window.confirm(t("license_confirm_delete_type", { name: lang === "ar" ? lt.name_ar : lt.name_en }))) return;
    setBusy(true); setErr(null);
    try {
      await api.delete(`/license-types/${lt.key}`);
      setItems((arr) => arr.filter((x) => x.key !== lt.key));
      await onChanged();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const patchNewRow = (uid, patch) => setNewRows((rows) => rows.map((r) => r._uid === uid ? { ...r, ...patch } : r));
  const addRow = () => setNewRows((rows) => [...rows, emptyRow()]);
  const removeRow = (uid) => setNewRows((rows) => rows.length === 1 ? [emptyRow()] : rows.filter((r) => r._uid !== uid));

  const saveAll = async () => {
    const nonEmpty = newRows.filter((r) => r.name_ar.trim() && r.name_en.trim());
    if (nonEmpty.length === 0) { setErr(t("license_err_names_required")); return; }
    setBusy(true); setErr(null);
    const created = [];
    const failed = [];
    for (const r of nonEmpty) {
      try {
        const res = await api.post("/license-types", { name_ar: r.name_ar, name_en: r.name_en, color: r.color, icon: r.icon, order: (items.length || 0) + created.length + 1 });
        created.push(res.data);
      } catch (e) {
        const d = e.response?.data?.detail;
        failed.push({ name: r.name_ar || r.name_en, msg: typeof d === "string" ? d : (d?.message === "duplicate_license_type_key" ? t("license_err_duplicate") : t("save_failed")) });
      }
    }
    if (created.length) setItems((arr) => [...arr, ...created]);
    setNewRows([emptyRow()]);
    if (failed.length) setErr(failed.map((f) => `${f.name}: ${f.msg}`).join(" · "));
    await onChanged();
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="manage-license-types-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden">
        <div className="bg-slate-800 text-white px-4 py-3 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2"><Settings2 size={16} /> {t("license_manage_types")}</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-manage-types">✕</button>
        </div>
        <div className="p-4 space-y-3 max-h-[75vh] overflow-y-auto">
          {err && <div className="text-sm bg-rose-50 border border-rose-200 rounded-lg p-2 text-rose-700">{err}</div>}
          <div className="text-xs text-slate-500">{t("license_manage_help")}</div>

          {items.map((lt) => {
            const c = colorOf(lt.color);
            const Icon = iconOf(lt.icon);
            return (
              <div key={lt.key} className={`border-2 rounded-xl p-3 ${c.border} ${c.bg}`} data-testid={`lt-row-${lt.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={16} className={c.icon} />
                  <div className="text-[11px] font-mono text-slate-500">key: {lt.key}</div>
                  {lt.is_builtin && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{t("license_builtin_badge")}</span>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-slate-500">{t("license_name_ar")}</label>
                    <input type="text" value={lt.name_ar} onChange={(e) => patchLocal(lt.key, { name_ar: e.target.value })} className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid={`lt-ar-${lt.key}`} />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500">{t("license_name_en")}</label>
                    <input type="text" value={lt.name_en} onChange={(e) => patchLocal(lt.key, { name_en: e.target.value })} className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm" style={{ direction: "ltr" }} data-testid={`lt-en-${lt.key}`} />
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <select value={lt.color} onChange={(e) => patchLocal(lt.key, { color: e.target.value })} className="border border-slate-300 rounded-lg px-2 py-1 text-xs bg-white" data-testid={`lt-color-${lt.key}`}>
                    {Object.keys(COLOR_PALETTE).map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <select value={lt.icon} onChange={(e) => patchLocal(lt.key, { icon: e.target.value })} className="border border-slate-300 rounded-lg px-2 py-1 text-xs bg-white" data-testid={`lt-icon-${lt.key}`}>
                    {Object.keys(ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <button onClick={() => saveOne(lt)} disabled={busy} className="btn btn-primary text-xs" data-testid={`lt-save-${lt.key}`}><Save size={11} /> {t("save")}</button>
                  {!lt.is_builtin && (
                    <button onClick={() => deleteOne(lt)} disabled={busy} className="btn text-xs bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100" data-testid={`lt-delete-${lt.key}`}><Trash2 size={11} /></button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Multi-row add section */}
          <div className="border-2 border-dashed border-teal-300 rounded-xl p-3 bg-teal-50/40">
            <div className="text-sm font-bold text-teal-800 mb-1 flex items-center gap-1"><Plus size={14} /> {t("license_add_new")}</div>
            <div className="text-[11px] text-teal-700 mb-3">{t("license_add_hint_multi_rows")}</div>
            <div className="space-y-3">
              {newRows.map((r, idx) => (
                <div key={r._uid} className="bg-white rounded-lg border border-teal-200 p-2.5" data-testid={`lt-new-row-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] font-bold text-teal-700">#{idx + 1}</div>
                    {newRows.length > 1 && (
                      <button onClick={() => removeRow(r._uid)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`lt-new-remove-${idx}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input type="text" value={r.name_ar} onChange={(e) => patchNewRow(r._uid, { name_ar: e.target.value })} placeholder={t("license_name_ar")} className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid={`lt-new-ar-${idx}`} />
                    <input type="text" value={r.name_en} onChange={(e) => patchNewRow(r._uid, { name_en: e.target.value })} placeholder={t("license_name_en")} className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm" style={{ direction: "ltr" }} data-testid={`lt-new-en-${idx}`} />
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <select value={r.color} onChange={(e) => patchNewRow(r._uid, { color: e.target.value })} className="border border-slate-300 rounded-lg px-2 py-1 text-xs bg-white" data-testid={`lt-new-color-${idx}`}>
                      {Object.keys(COLOR_PALETTE).map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                    <select value={r.icon} onChange={(e) => patchNewRow(r._uid, { icon: e.target.value })} className="border border-slate-300 rounded-lg px-2 py-1 text-xs bg-white" data-testid={`lt-new-icon-${idx}`}>
                      {Object.keys(ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button onClick={addRow} className="btn btn-secondary text-xs" data-testid="lt-new-add-row">
                <Plus size={12} /> {t("license_add_another_row")}
              </button>
              <button onClick={saveAll} disabled={busy} className="btn btn-primary text-xs" data-testid="lt-new-save-all">
                <Save size={12} /> {t("license_save_all")}
              </button>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-slate-50 border-t flex items-center justify-end">
          <button onClick={onClose} className="btn btn-secondary" data-testid="close-manage-types-2">{t("close")}</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Add Branch Modal ─────────── */
function AddBranchModal({ admins: initialAdmins, areaManagers, isSuper, currentUserId, onClose, onCreated, t }) {
  const [form, setForm] = useState({ name: "", code: "", admin_id: "", area_manager_id: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  // local list of admins/supervisors so we can append newly-created ones inline
  const [admins, setAdmins] = useState(initialAdmins);
  const [showNewSup, setShowNewSup] = useState(false);
  const [newSup, setNewSup] = useState({ name: "", phone: "", email: "" });
  const [creatingSup, setCreatingSup] = useState(false);
  const [supErr, setSupErr] = useState("");

  // Effective AM id used for scoping supervisors
  const effectiveAmId = isSuper ? (form.area_manager_id || "") : currentUserId;
  const supervisorsForAm = effectiveAmId
    ? admins.filter((a) => a.area_manager_id === effectiveAmId)
    : (isSuper ? [] : admins);

  // When area manager changes, clear the supervisor selection
  useEffect(() => {
    setForm((f) => ({ ...f, admin_id: "" }));
    setShowNewSup(false);
  }, [form.area_manager_id]);

  const createSupervisor = async () => {
    setSupErr("");
    if (!newSup.name.trim()) { setSupErr(t("required_error") || "الاسم مطلوب"); return; }
    if (isSuper && !form.area_manager_id) {
      setSupErr("اختر مدير المنطقة أولاً");
      return;
    }
    setCreatingSup(true);
    try {
      const payload = { ...newSup };
      if (isSuper) payload.area_manager_id = form.area_manager_id;
      const res = await api.post("/admins", payload);
      setAdmins((cur) => [...cur, res.data]);
      setForm((f) => ({ ...f, admin_id: res.data.id }));
      setShowNewSup(false);
      setNewSup({ name: "", phone: "", email: "" });
    } catch (e) {
      const d = e.response?.data?.detail;
      setSupErr(typeof d === "string" ? d : (d?.message || e.message || t("save_failed")));
    } finally { setCreatingSup(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return;
    if (isSuper && !form.area_manager_id) { setErr("اختر مدير المنطقة"); return; }
    setSaving(true); setErr("");
    try {
      const payload = { ...form, admin_id: form.admin_id || null };
      if (!isSuper) delete payload.area_manager_id;
      const res = await api.post("/branches", payload);
      onCreated(res.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d && typeof d === "object") {
        if (d.message === "duplicate_branch_code") {
          setErr(t("duplicate_branch_code_error").replace("{code}", d.code || "").replace("{area_manager_name}", d.area_manager_name || "—"));
        } else if (d.message === "duplicate_branch_name") {
          setErr(t("duplicate_branch_name_error").replace("{name}", d.name || "").replace("{area_manager_name}", d.area_manager_name || "—"));
        } else {
          setErr(typeof d === "string" ? d : t("save_failed"));
        }
      } else {
        setErr(typeof d === "string" ? d : (e.message || t("save_failed")));
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop" onClick={onClose} data-testid="add-branch-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="brand-gradient text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
          <div className="font-bold flex items-center gap-2"><Building2 size={18} /> {t("add_new_branch")}</div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/15" data-testid="add-branch-close"><Plus size={16} className="rotate-45" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">{t("branch_name")}</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t("branch_name_placeholder")}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              autoFocus
              data-testid="add-branch-name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">{t("branch_code")}</label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder={t("branch_code_placeholder")}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="add-branch-code"
            />
          </div>

          {/* Area Manager — super admin only */}
          {isSuper && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">👑 {t("license_am_all").replace("كل ", "").replace("All ", "")} *</label>
              <select
                value={form.area_manager_id}
                onChange={(e) => setForm({ ...form, area_manager_id: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="add-branch-am"
              >
                <option value="">— اختر مدير المنطقة —</option>
                {areaManagers.map((am) => (
                  <option key={am.id} value={am.id}>{am.full_name || am.username}</option>
                ))}
              </select>
            </div>
          )}

          {/* Supervisor — filtered by selected AM */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-slate-600">{t("nav_admins")}</label>
              {(!isSuper || form.area_manager_id) && (
                <button
                  type="button"
                  onClick={() => setShowNewSup((v) => !v)}
                  className="text-[11px] font-bold text-teal-700 hover:text-teal-900"
                  data-testid="toggle-new-sup"
                >
                  {showNewSup ? "إلغاء" : "+ مشرف جديد"}
                </button>
              )}
            </div>
            {!showNewSup && (
              <select
                value={form.admin_id}
                onChange={(e) => setForm({ ...form, admin_id: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                disabled={isSuper && !form.area_manager_id}
                data-testid="add-branch-admin"
              >
                <option value="">{"— " + t("license_sup_none") + " —"}</option>
                {supervisorsForAm.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
              </select>
            )}
            {showNewSup && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-2">
                <input
                  type="text"
                  placeholder="اسم المشرف *"
                  value={newSup.name}
                  onChange={(e) => setNewSup({ ...newSup, name: e.target.value })}
                  className="w-full border border-teal-300 rounded px-2 py-1.5 text-sm bg-white"
                  data-testid="new-sup-name"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="جوال (اختياري)"
                    value={newSup.phone}
                    onChange={(e) => setNewSup({ ...newSup, phone: e.target.value })}
                    className="border border-teal-300 rounded px-2 py-1.5 text-sm bg-white"
                    data-testid="new-sup-phone"
                  />
                  <input
                    type="email"
                    placeholder="إيميل (اختياري)"
                    value={newSup.email}
                    onChange={(e) => setNewSup({ ...newSup, email: e.target.value })}
                    className="border border-teal-300 rounded px-2 py-1.5 text-sm bg-white"
                    data-testid="new-sup-email"
                  />
                </div>
                {supErr && <div className="text-xs bg-rose-50 border border-rose-200 rounded p-1.5 text-rose-700" data-testid="new-sup-error">{supErr}</div>}
                <button
                  type="button"
                  onClick={createSupervisor}
                  disabled={creatingSup || !newSup.name.trim()}
                  className="btn btn-primary text-xs w-full"
                  data-testid="save-new-sup"
                >
                  {creatingSup ? "…" : "حفظ المشرف"}
                </button>
              </div>
            )}
          </div>

          {err && <div className="text-sm bg-rose-50 border border-rose-200 rounded-lg p-2 text-rose-700" data-testid="add-branch-error">{err}</div>}
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t flex items-center justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} className="btn btn-secondary" data-testid="add-branch-cancel">{t("cancel")}</button>
          <button onClick={save} disabled={saving || !form.name.trim() || (isSuper && !form.area_manager_id)} className="btn btn-primary" data-testid="add-branch-save">
            <Save size={14} /> {saving ? "…" : t("save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Main tracker ─────────── */
export default function LicensesTracker() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const isSuper = user?.role === "super_admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [branches, setBranches] = useState([]);
  const [licenses, setLicenses] = useState({ expired: [], warning: [], missing: [], valid: [], total_branches: 0, warning_days: 30 });
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [areaManagers, setAreaManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState("all");
  const [amFilter, setAmFilter] = useState("all");
  const [supFilter, setSupFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [addBranchOpen, setAddBranchOpen] = useState(false);
  const canAddBranch = user?.role === "super_admin" || user?.role === "area_manager";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [
        api.get("/branches"),
        api.get("/licenses"),
        api.get("/admins"),
        api.get("/license-types"),
      ];
      if (isSuper) promises.push(api.get("/area-managers").catch(() => ({ data: [] })));
      const results = await Promise.all(promises);
      const [br, lic, ad, lt, am] = results;
      setBranches(br.data.filter((b) => !b.is_vacation && !b.is_admin));
      setLicenses(lic.data);
      setAdmins(ad.data || []);
      setLicenseTypes(lt.data || []);
      setAreaManagers(am?.data || []);
    } finally { setLoading(false); }
  }, [isSuper]);

  useEffect(() => { load(); }, [load]);

  // Deep-link support: /licenses?q=<code>&edit=<branch_id>
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId) return;
    if (loading || branches.length === 0) return;
    const target = branches.find((b) => b.id === editId);
    if (target) setEditing(target);
    // clear params so refresh won't reopen the modal
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    next.delete("focus");
    setSearchParams(next, { replace: true });
  }, [loading, branches, searchParams, setSearchParams]);

  const branchStatus = (b) => {
    const statuses = licenseTypes.map((lt) => statusOf(daysUntil(b.licenses?.[lt.key]?.expiry_g)));
    if (statuses.includes("expired")) return "expired";
    if (statuses.includes("warning")) return "warning";
    if (statuses.includes("missing")) return "missing";
    return "valid";
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return branches.filter((b) => {
      if (q) {
        const hay = `${b.name || ""} ${b.code || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== "all" && branchStatus(b) !== statusFilter) return false;
      if (amFilter !== "all" && b.area_manager_id !== amFilter) return false;
      if (supFilter !== "all") {
        if (supFilter === "__none__") { if (b.admin_id) return false; }
        else if (b.admin_id !== supFilter) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches, search, statusFilter, amFilter, supFilter, licenseTypes]);

  const supervisorsFiltered = amFilter === "all" ? admins : admins.filter((a) => a.area_manager_id === amFilter);
  const clearFilters = () => { setSearch(""); setStatusFilter("all"); setAmFilter("all"); setSupFilter("all"); };
  const hasActiveFilter = search || statusFilter !== "all" || amFilter !== "all" || supFilter !== "all";

  const kpis = [
    { label: t("licenses_kpi_total"),   value: licenses.total_branches, tone: "text-slate-800", bg: "bg-slate-50", Icon: Building2 },
    { label: t("licenses_kpi_expired"), value: licenses.expired.length, tone: "text-rose-700",  bg: "bg-rose-50",  Icon: XCircle },
    { label: t("licenses_kpi_warning"), value: licenses.warning.length, tone: "text-amber-700", bg: "bg-amber-50", Icon: Clock },
    { label: t("licenses_kpi_missing"), value: licenses.missing.length, tone: "text-slate-600", bg: "bg-slate-100", Icon: FileQuestion },
  ];

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const exportExcel = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/licenses/export/xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `branch_licenses_${stamp}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-5" data-testid="licenses-tracker">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-2">
            <Building2 className="text-fuchsia-600" size={28} />
            {t("licenses_title")}
          </h1>
          <p className="text-sm text-slate-500">{t("licenses_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canAddBranch && (
            <button onClick={() => setAddBranchOpen(true)} className="btn btn-primary text-sm" data-testid="licenses-add-branch">
              <Plus size={14} /> {t("add_branch")}
            </button>
          )}
          <button
            onClick={exportExcel}
            disabled={loading || branches.length === 0}
            className="btn btn-secondary text-sm"
            data-testid="licenses-export-excel"
            title="تنزيل Excel"
          >
            <FileSpreadsheet size={14} /> Excel
          </button>
          {isSuper && (
            <button onClick={() => setManageOpen(true)} className="btn btn-secondary text-sm" data-testid="open-manage-types">
              <Settings2 size={14} /> {t("license_manage_types")}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className={`${k.bg} rounded-2xl p-4 border border-slate-100`}>
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-3xl font-black ${k.tone}`} data-testid={`kpi-${k.label}`}>{k.value}</div>
                <div className="text-xs text-slate-500 mt-1">{k.label}</div>
              </div>
              <k.Icon className={`${k.tone} opacity-60`} size={26} />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute top-2.5 start-2 text-slate-400" />
            <input type="text" placeholder={t("license_search_placeholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full ps-8 pe-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="license-search" />
          </div>
          <Filter size={14} className="text-slate-400" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white min-w-[130px]" data-testid="license-status-filter">
            <option value="all">{t("license_status_all")}</option>
            <option value="expired">{t("license_status_expired")}</option>
            <option value="warning">{t("license_status_warning")}</option>
            <option value="valid">{t("license_status_valid")}</option>
            <option value="missing">{t("license_status_missing_short")}</option>
          </select>
          <select value={amFilter} onChange={(e) => { setAmFilter(e.target.value); setSupFilter("all"); }} className={`border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white min-w-[160px] ${!isSuper ? "hidden" : ""}`} data-testid="license-am-filter">
            <option value="all">{"👑 " + t("license_am_all")}</option>
            {areaManagers.map((am) => <option key={am.id} value={am.id}>{"👑 " + (am.full_name || am.username)}</option>)}
          </select>
          <select value={supFilter} onChange={(e) => setSupFilter(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white min-w-[160px]" data-testid="license-sup-filter">
            <option value="all">{t("license_sup_all")}</option>
            <option value="__none__">{"— " + t("license_sup_none") + " —"}</option>
            {supervisorsFiltered.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button onClick={load} className="btn btn-secondary text-xs" data-testid="license-refresh"><RefreshCw size={13} /> {t("refresh")}</button>
          {hasActiveFilter && (
            <button onClick={clearFilters} className="btn text-xs bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100" data-testid="license-clear-filters">{t("license_clear_filters")}</button>
          )}
        </div>
        <div className="text-[11px] text-slate-500 mt-2">{t("license_showing_x_of_y", { x: filtered.length, y: branches.length })}</div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-start font-bold text-slate-600">{t("license_col_branch")}</th>
                {isSuper && <th className="p-3 text-start font-bold text-slate-600 hidden md:table-cell">{t("license_col_area_manager")}</th>}
                <th className="p-3 text-start font-bold text-slate-600 hidden md:table-cell">{t("license_col_supervisor")}</th>
                {licenseTypes.map((lt) => {
                  const Icon = iconOf(lt.icon);
                  const c = colorOf(lt.color);
                  return (
                    <th key={lt.key} className="p-3 text-start font-bold text-slate-600">
                      <span className="inline-flex items-center gap-1"><Icon size={13} className={c.icon} /> {lang === "ar" ? lt.name_ar : lt.name_en}</span>
                    </th>
                  );
                })}
                <th className="p-3 text-start font-bold text-slate-600"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3 + licenseTypes.length + (isSuper ? 1 : 0)} className="p-8 text-center text-slate-400">…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={3 + licenseTypes.length + (isSuper ? 1 : 0)} className="p-8 text-center text-slate-400" data-testid="license-empty">{t("license_no_matches")}</td></tr>
              ) : filtered.map((b, i) => {
                const am = areaManagers.find((x) => x.id === b.area_manager_id);
                const sup = admins.find((x) => x.id === b.admin_id);
                return (
                  <tr key={b.id} className={i % 2 ? "bg-slate-50/40" : ""} data-testid={`license-row-${b.id}`}>
                    <td className="p-3">
                      <div className="font-semibold text-slate-800">{b.name}</div>
                      {b.code && <div className="text-[11px] text-slate-400">[{b.code}]</div>}
                    </td>
                    {isSuper && (
                      <td className="p-3 text-slate-600 hidden md:table-cell">{am ? <span>👑 {am.full_name || am.username}</span> : "—"}</td>
                    )}
                    <td className="p-3 text-slate-600 hidden md:table-cell">{sup?.name || "—"}</td>
                    {licenseTypes.map((lt) => {
                      const attKey = lt.key === "business" ? "business_license" : (lt.key === "civil_defense" ? "civil_defense" : lt.key);
                      const att = (b.license_attachments || []).find((a) => a.kind === attKey);
                      const url = att ? `${backendBase}/api/branches/${b.id}/license-attachments/${att.id}` : null;
                      const c = colorOf(lt.color);
                      const entry = b.licenses?.[lt.key];
                      return (
                        <td key={lt.key} className="p-3">
                          <div className="flex items-center gap-2">
                            {url ? (
                              <a href={url} target="_blank" rel="noopener noreferrer" className={`w-10 h-10 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0 hover:ring-2 ${c.ring} transition`} title={t("license_open_image")} data-testid={`row-thumb-${lt.key}-${b.id}`}>
                                {att.content_type?.startsWith("image/") ? (
                                  <img src={url} alt={lang === "ar" ? lt.name_ar : lt.name_en} className="w-full h-full object-cover" />
                                ) : (
                                  <div className={`w-full h-full flex items-center justify-center ${c.icon}`}><ImageIcon size={16} /></div>
                                )}
                              </a>
                            ) : null}
                            <div>
                              <div className="font-mono text-slate-800 text-sm">{formatH(entry?.expiry_hijri)}</div>
                              <div className="mt-0.5"><Pill days={daysUntil(entry?.expiry_g)} t={t} /></div>
                            </div>
                          </div>
                        </td>
                      );
                    })}
                    <td className="p-3">
                      <button onClick={() => setEditing(b)} className="text-slate-400 hover:text-teal-700 p-1.5 rounded-lg hover:bg-teal-50" title={t("edit")} data-testid={`edit-license-${b.id}`}>
                        <Edit3 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <LicenseEditModal
          branch={editing}
          licenseTypes={licenseTypes}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          t={t}
          lang={lang}
        />
      )}
      {manageOpen && isSuper && (
        <ManageLicenseTypesModal
          types={licenseTypes}
          onClose={() => setManageOpen(false)}
          onChanged={async () => { await load(); }}
          t={t}
          lang={lang}
        />
      )}
      {addBranchOpen && canAddBranch && (
        <AddBranchModal
          admins={admins}
          areaManagers={areaManagers}
          isSuper={isSuper}
          currentUserId={user?.id}
          onClose={() => setAddBranchOpen(false)}
          onCreated={async (created) => {
            setAddBranchOpen(false);
            await load();
            // Immediately open the edit modal on the newly created branch so the user can enter license dates
            if (created?.id) {
              // Wait a tick so `branches` state is populated with the new record
              setTimeout(() => {
                setBranches((cur) => {
                  const target = cur.find((b) => b.id === created.id);
                  if (target) setEditing(target);
                  return cur;
                });
              }, 0);
            }
          }}
          t={t}
        />
      )}
    </div>
  );
}
